import { ComponentPropsWithoutRef } from "react";
import { Table as TanstackTable } from "@tanstack/react-table";
import cx from "classnames";

import { getCommonPinningStyles } from "../../internal/getCommonPinningStyles";
import { getCommonPinningClasses } from "../../internal/getCommonPinningClasses";

import { ColumnDef } from "../../types";
import { DataTableProps, type DataTableRowData } from "../DataTable";
import { TableFooter } from "../../base/TableFooter";
import { DataTableFooterCell } from "./cells/DataTableFooterCell";

import styles from "./DataTableFooter.module.scss";
import { useColumnOrder } from "./useColumnOrder";

/**
 * Props for the default/configured table footer rows
 * @extends TanstackTable<T>
 * @extends ColumnDef<T>[]
 */
export type DefaultTableFooterRowsProps<T extends DataTableRowData> = {
  table: TanstackTable<T>;
  columns: ColumnDef<T>[];
};

/**
 * Props for the custom table footer rows
 * @extends DataTableProps<T>
 */
export type CustomTableFooterRowsProps<T extends DataTableRowData> = {
  columnCount: number;
  customFooter: DataTableProps<T>["customFooter"];
  table: TanstackTable<T>;
};

/**
 * Props for the DataTableFooter component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DataTableFooterProps<T extends DataTableRowData> = {
  /**
   * The table instance from @tanstack/react-table to render
   */
  table: TanstackTable<T>;
  /**
   * The columns of the table
   */
  columns: ColumnDef<T>[];
  /**
   * The custom footer of the table
   */
  customFooter?: DataTableProps<T>["customFooter"];
} & ComponentPropsWithoutRef<"div">;

function DefaultTableFooterRows<T extends DataTableRowData>({
  table,
  columns,
}: DefaultTableFooterRowsProps<T>) {
  // Recursively find a column by ID in the column tree
  const findColumnById = (
    cols: ColumnDef<T>[],
    id: string,
  ): ColumnDef<T> | undefined => {
    for (const col of cols) {
      if (col.id === id) {
        return col;
      }
      if (col.columns && Array.isArray(col.columns)) {
        const found = findColumnById(col.columns, id);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  };

  // Recursively get the maximum number of footer rows from a column and its nested columns
  const getMaxFooterRows = (column: ColumnDef<T>): number => {
    let currentMax = 0;

    // Check current column's footer content
    if (Array.isArray(column.footerContent)) {
      currentMax = column.footerContent.length;
    } else if (column.footerContent) {
      currentMax = 1;
    }

    // Recursively check nested columns
    if (column.columns && Array.isArray(column.columns)) {
      const nestedMax = Math.max(
        ...column.columns.map((nestedCol) => getMaxFooterRows(nestedCol)),
        0,
      );
      currentMax = Math.max(currentMax, nestedMax);
    }

    return currentMax;
  };

  const maxFooterRows = Math.max(
    ...columns.map((column) => getMaxFooterRows(column)),
    1,
  );

  const { columnOrder } = useColumnOrder({ table });

  return Array.from({ length: maxFooterRows }, (_, footerIndex) => {
    const rowIndex =
      footerIndex +
      table.getHeaderGroups().length +
      table.getRowModel().rows.length;
    return (
      <div
        key={`footer-row-${rowIndex}`}
        role="row"
        data-anv="table-footer-row"
        className={styles["data-table-footer-row"]}
        aria-rowindex={rowIndex + 1}
      >
        {table
          .getFlatHeaders()
          .filter((header) => {
            const column = findColumnById(columns, header.column.id);
            // Filter out columns that have nested columns (column groups)
            return !column?.columns || column.columns.length === 0;
          })
          .map((header) => {
            const column = findColumnById(columns, header.column.id);

            let footerContent = null;
            if (Array.isArray(column?.footerContent)) {
              footerContent = column.footerContent[footerIndex] || null;
            } else if (column?.footerContent && footerIndex === 0) {
              footerContent = column.footerContent;
            }

            const columnIndex = columnOrder.indexOf(header.column.getIndex());

            return (
              <DataTableFooterCell
                key={header.id}
                className={getCommonPinningClasses(header.column)}
                style={getCommonPinningStyles(header.column)}
                cellPosition={{ rowIndex, columnIndex }}
              >
                {footerContent}
              </DataTableFooterCell>
            );
          })}
      </div>
    );
  });
}

function CustomTableFooterRows<T extends DataTableRowData>({
  customFooter,
  columnCount,
  table,
}: CustomTableFooterRowsProps<T>) {
  return customFooter?.map((footerRow, footerIndex) => {
    const rowIndex =
      footerIndex +
      table.getHeaderGroups().length +
      table.getRowModel().rows.length;
    return (
      <div
        key={footerIndex}
        role="row"
        data-anv="table-footer-row"
        className={styles["data-table-footer-row"]}
        aria-rowindex={rowIndex + 1}
      >
        {(() => {
          let columnIndex = 0;
          return Array.from({
            length:
              // total columns
              columnCount -
              // minus columns merged using colSpan
              footerRow.reduce((acc, curr) => acc + curr.colSpan, 0) +
              // plus columns added in the custom footer row
              footerRow.length,
          }).map((_, index) => {
            const footer = footerRow[index] ?? { content: "", colSpan: 1 };
            // Use grid-column span for colspan cells
            const gridColumn =
              footer.colSpan > 1 ? `span ${footer.colSpan}` : undefined;

            const cell = (
              <DataTableFooterCell
                key={index}
                colSpan={footer.colSpan}
                style={{
                  gridColumn,
                }}
                cellPosition={{ rowIndex, columnIndex }}
              >
                {footer.content}
              </DataTableFooterCell>
            );

            // Advance the column index by the colSpan for the next iteration
            columnIndex += footer.colSpan;
            return cell;
          });
        })()}
      </div>
    );
  });
}

/**
 * Data table footer, which can be either a configured or custom footer
 * @param props - The props for the DataTableFooter component
 * @param props.table - The table instance from @tanstack/react-table to render
 * @param props.customFooter - The custom footer of the table
 * @param props.columns - The columns of the table
 */
export function DataTableFooter<T extends DataTableRowData>({
  table,
  customFooter,
  columns,
  ...rest
}: DataTableFooterProps<T>) {
  const columnCount = table.getFlatHeaders().length;
  const classes = cx(styles["data-table-footer"], rest.className);

  return (
    <TableFooter type="data-table" {...rest} className={classes}>
      {customFooter ? (
        <CustomTableFooterRows
          table={table}
          customFooter={customFooter}
          columnCount={columnCount}
        />
      ) : (
        <DefaultTableFooterRows table={table} columns={columns} />
      )}
    </TableFooter>
  );
}
